/**
 * A node's own sign-in status, `GET /local/auth` (D20; unauthenticated, outside
 * the route table): whether its cloud link is down — the only time offline
 * sign-in (an offline PIN or the site's emergency account) is accepted. The
 * login page polls it, so "Offline sign-in" appears when the link drops and
 * goes away when it returns.
 */
import type { FetchPort } from '@acp/api-client/ports.js';
import { localAuthStatusWireSchema, type LocalAuthStatusWire } from '@acp/api-contract/offlineAuth.js';
import { parseContract } from '@acp/api-contract/parsing.js';
import { queryOptions } from '@tanstack/react-query';

/** How often the login page asks. The node calls its link down after 90 s of silence, so 15 s is prompt enough. */
export const LOCAL_AUTH_POLL_MS = 15_000;

/** `GET /local/auth` on the node at `apiUrl`. */
export async function readLocalAuth(fetch: FetchPort, apiUrl: string): Promise<LocalAuthStatusWire> {
  const response = await fetch(`${apiUrl}/local/auth`, { method: 'GET', headers: { accept: 'application/json' }, body: null });
  const text = await response.text();
  if (response.status !== 200) throw new Error(`the node's sign-in status is unavailable (HTTP ${String(response.status)})`);
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error('the node answered its sign-in status with something that is not JSON');
  }
  // Tolerant (D37): asked during an outage, when the node and this SPA are most likely on different releases.
  const parsed = parseContract(localAuthStatusWireSchema, json, 'tolerant');
  if (!parsed.success) throw new Error('the node answered its sign-in status in a shape this app does not know');
  return parsed.data;
}

/** The status query; disabled (never fetched) when the page is not served by a node (`read` null). */
export function localAuthQuery(read: (() => Promise<LocalAuthStatusWire>) | null) {
  return queryOptions({
    queryKey: ['local-auth'] as const,
    queryFn: async (): Promise<LocalAuthStatusWire> => {
      if (read === null) throw new Error('this page is not served by a node');
      return read();
    },
    enabled: read !== null,
    refetchInterval: LOCAL_AUTH_POLL_MS,
    retry: false,
  });
}
